/**
 * Dashboard overview view-model (NAU-247).
 *
 * Assembles the redesigned dashboard's data from the data that ALREADY exists
 * client-side (scheduled questionnaires, follow-up actions, compliance defs) —
 * i.e. the "client-side merge" half of the hybrid plan. Figures that need a
 * backend aggregation (status, insights, the on-time streak) come from their
 * own dashboard endpoints rather than from here.
 */

import type {
  ComplianceTaskDefinitionResponseDto,
  FollowUpActionResponseDto,
  OpenIncidentResponseDto,
  ScheduledQuestionnaireListItemDto,
} from "@/generated/model";
import {
  type DashboardStatus,
  getInitials,
} from "@/lib/dashboard/traffic-light";
import { formatUkDate, isUkToday, toUkDateStr } from "@/lib/date-utils";

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export interface KpiTileData {
  value: number;
  status: DashboardStatus;
}

export interface DashboardKpis {
  completion: {
    rate: number;
    completed: number;
    total: number;
    status: DashboardStatus;
    /** Percentage-point change vs the previous period of equal length; null until both periods load. */
    deltaVsLastWeek: number | null;
  };
  missedOverdue: KpiTileData;
  issuesFlagged: KpiTileData;
  openActions: KpiTileData;
}

export interface RecentlyCompletedItem {
  id: string;
  title: string;
  timeLabel: string;
  actorInitials: string;
  /** Full name of the completer, surfaced as the avatar tooltip / label */
  actorName?: string;
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const isCompletedSq = (item: ScheduledQuestionnaireListItemDto) =>
  item.status === "COMPLETED" || item.status === "COMPLETED_LATE";

/* ------------------------------------------------------------------ *
 * Builders
 * ------------------------------------------------------------------ */

export interface OverviewInputs {
  statsItems: ScheduledQuestionnaireListItemDto[];
  dayItems: ScheduledQuestionnaireListItemDto[];
  actions: FollowUpActionResponseDto[];
  complianceDefinitions: ComplianceTaskDefinitionResponseDto[];
  complianceOverdueCount: number;
  /** Open out-of-range fridge incidents (NAU-219) surfaced in the priority list */
  openIncidents: OpenIncidentResponseDto[];
}

/**
 * The four summary-card counts are now served by the backend `/dashboard/status`
 * metrics (NAU-263) so they read the single status evaluation and reconcile with
 * the priority list. The card view-model is built from that payload by
 * `kpisFromMetrics` in `card-status.ts`; there is no client-side count path.
 */

export function buildRecentlyCompleted(
  inputs: OverviewInputs,
): RecentlyCompletedItem[] {
  const { dayItems } = inputs;
  return dayItems
    .filter(
      (i) =>
        isCompletedSq(i) && i.completedAt && isUkToday(new Date(i.completedAt)),
    )
    .sort((a, b) => (b.completedAt! > a.completedAt! ? 1 : -1))
    .slice(0, 5)
    .map((i) => {
      const user = i.completedBy?.[0];
      const name = user
        ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
        : "";
      const completedAt = new Date(i.completedAt!);
      const timeLabel = isUkToday(completedAt)
        ? `Today, ${formatUkDate(completedAt, "h:mmaaa")}`
        : `${toUkDateStr(completedAt)}, ${formatUkDate(completedAt, "h:mmaaa")}`;
      return {
        id: i.id,
        title: i.template.name,
        timeLabel,
        actorInitials: name ? getInitials(name) : "?",
        actorName: name || undefined,
      };
    });
}
